import React, { useState, forwardRef } from 'react';
import { FieldRenderProps } from 'react-final-form';
import {
  FormFieldWrapper,
  IconButton,
  InputWrapper,
  StyledInput,
} from './styles';
import { Condition } from '../../condition';
import { Image } from '../../image';
import { Box } from '@/shared/ui/box';
import { LinkRegularSecondary } from '../../typography';

interface CustomFormFieldProps extends FieldRenderProps<string> {
  label: string;
  resetPasswordSlot?: React.ReactNode;
  placeholderColor?: string;
  errorColor?: string;
  id?: string;
  bgColor?: string;
  small?: boolean;
  hasBackspace?: boolean;
  onInputChange?: (value: string) => void;
}

const FormField = forwardRef<HTMLInputElement, CustomFormFieldProps>(
  (
    {
      input,
      meta,
      label,
      resetPasswordSlot,
      placeholderColor,
      errorColor,
      id,
      bgColor,
      small,
      hasBackspace,
      onInputChange,
      ...rest
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword);
    };

    const backspace = () => {
      const newValue = input.value.slice(0, -1);
      input.onChange(newValue);
      if (onInputChange) {
        onInputChange(newValue);
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      input.onChange(e);
      if (onInputChange) {
        onInputChange(e.target.value);
      }
    };

    const { type, ...restInput } = input;

    return (
      <FormFieldWrapper $errorColor={errorColor}>
        <Condition match={id !== 'auth-email-password'}>
          {label && <label>{label}</label>}
        </Condition>
        <Condition match={id === 'auth-email-password'}>
          {label && (
            <Box
              $paddingBottom={4}
              $alignItems="center"
              $display="flex"
              $justifyContent="space-between"
            >
              <Box>{label}</Box>
              <LinkRegularSecondary color="#AFAFAF">
                {resetPasswordSlot}
              </LinkRegularSecondary>
            </Box>
          )}
        </Condition>
        <Condition match={type !== 'password'}>
          <InputWrapper>
            <StyledInput
              $bgColor={bgColor}
              $small={small}
              $placeholderColor={placeholderColor}
              $error={meta.touched && meta.error}
              $valid={meta.touched && meta.valid}
              autoComplete="off"
              id={id}
              ref={ref} // Передаем ref в StyledInput
              {...input}
              {...rest}
              onChange={handleChange}
            />
            <Condition match={input.value && hasBackspace}>
              <IconButton
                type="button"
                variant="transparent"
                onClick={backspace}
              >
                <Image
                  width={20}
                  height={20}
                  src="/images/icons/backspace.svg"
                />
              </IconButton>
            </Condition>
          </InputWrapper>
        </Condition>
        <Condition match={type === 'password'}>
          <InputWrapper>
            <StyledInput
              $bgColor={bgColor}
              $error={meta.touched && meta.error}
              type={showPassword ? 'text' : 'password'}
              autoComplete="off"
              id={id}
              ref={ref} // Передаем ref в StyledInput
              {...restInput}
              {...rest}
            />
            <IconButton
              type="button"
              variant="transparent"
              onClick={togglePasswordVisibility}
            >
              <Condition match={showPassword}>
                <Image
                  width={20}
                  height={20}
                  src="/images/icons/eye-opened.svg"
                />
              </Condition>
              <Condition match={!showPassword}>
                <Image
                  width={20}
                  height={20}
                  src="/images/icons/eye-closed.svg"
                />
              </Condition>
            </IconButton>
          </InputWrapper>
        </Condition>
        {meta.error &&
          meta.touched &&
          meta.error.map((error) => (
            <div key={error} className="error">
              {error}
            </div>
          ))}
      </FormFieldWrapper>
    );
  }
);

FormField.displayName = 'FormField'; // Устанавливаем displayName для отладки

export { FormField };
